/**
 * cardioSelectionEngine.js — Moteur de sélection déterministe (aucune IA) du parcours "Cardio
 * guidé" : construit directement des séances (méthode + rounds/effort/repos + exercices réels
 * de la Bibliothèque V2) qui respectent la durée cible de séance (± TOLERANCE_MINUTES), dans le
 * même esprit que le moteur musculation (`exerciseSelectionEngine.js`) — jamais de boîte noire,
 * toujours transparent sur le résultat final via `estimateCardioSessionMinutes`.
 */
import { estimateCardioSessionSeconds } from './cardioTimeEstimator';

export const TOLERANCE_MINUTES = 5;

const LEVEL_ADJUST = {
  debutant: { roundsMult: 0.75, workMult: 0.85, restMult: 1.3 },
  intermediaire: { roundsMult: 1, workMult: 1, restMult: 1 },
  avance: { roundsMult: 1.25, workMult: 1.1, restMult: 0.75 },
};

// Bornes de sécurité par méthode — Tabata reste un protocole scientifique figé (8x20s/10s).
const ROUND_BOUNDS = { hiit: [4, 14], tabata: [8, 8], emom: [6, 24], amrap: [1, 1], intervals: [3, 12] };

const nameOf = (e) => e.name_fr || e.name;
const round5 = (s) => Math.max(5, Math.round(s / 5) * 5);

// PRNG déterministe (seedé) — permet un mélange DIFFÉRENT à chaque clic "Régénérer" (avant :
// un simple rotate d'1 index sur le pool ne variait presque jamais le résultat perçu par le
// client, remonté par le testing_agent 14/09/2026).
const seededShuffle = (arr, seed) => {
  let s = seed || 1;
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const pickMethodsForSession = (methods, sessionIdx) => {
  if (!methods.length) return [];
  if (methods.length === 1) return [methods[0]];
  const first = methods[sessionIdx % methods.length];
  const second = methods[(sessionIdx + 1) % methods.length];
  return first.id === second.id ? [first] : [first, second];
};

const buildBlockExercises = (pool, count, usedNames) => {
  if (!pool.length) return [];
  const picked = [];
  for (const ex of pool) {
    if (picked.length >= count) break;
    const n = nameOf(ex);
    if (usedNames.has(n)) continue;
    picked.push(ex);
    usedNames.add(n);
  }
  // Pool épuisé (matériel très restrictif) : autorise la répétition plutôt qu'un bloc vide.
  let i = 0;
  while (picked.length < count) { picked.push(pool[i % pool.length]); i += 1; }
  return picked.map((ex) => ({ name: nameOf(ex), notes: null }));
};

const buildBlock = (method, adj, pool, usedNames, targetBlockSeconds) => {
  if (method.id === 'amrap') {
    const cap = round5(Math.max(180, Math.min(1200, targetBlockSeconds || 480)));
    const exercises = buildBlockExercises(pool, 4, usedNames);
    return { method: 'amrap', rounds: 1, work_seconds: cap, rest_seconds: 0, time_cap_seconds: cap, exercises };
  }
  const [minR, maxR] = ROUND_BOUNDS[method.id] || [1, 12];
  const rounds = method.id === 'tabata' ? 8 : Math.min(maxR, Math.max(minR, Math.round((method.default_rounds || 6) * adj.roundsMult)));
  const work = method.id === 'tabata' ? 20 : round5(Math.round((method.default_work_seconds || 30) * adj.workMult));
  const rest = method.id === 'tabata' ? 10 : round5(Math.round((method.default_rest_seconds || 15) * adj.restMult));
  const exCount = method.id === 'emom' ? 4 : method.id === 'tabata' ? 1 : 3;
  const exercises = buildBlockExercises(pool, exCount, usedNames);
  return { method: method.id, rounds, work_seconds: work, rest_seconds: rest, time_cap_seconds: null, exercises };
};

const totalSeconds = (session) => estimateCardioSessionSeconds(session);

const adjustBlock = (block, grow) => {
  if (block.method === 'tabata') return false;
  if (block.method === 'amrap') {
    const newCap = block.time_cap_seconds + (grow ? 60 : -60);
    if (newCap < 180 || newCap > 1200) return false;
    block.time_cap_seconds = newCap; block.work_seconds = newCap;
    return true;
  }
  const [minR, maxR] = ROUND_BOUNDS[block.method] || [1, 20];
  const newRounds = block.rounds + (grow ? 1 : -1);
  if (newRounds < minR || newRounds > maxR) return false;
  block.rounds = newRounds;
  return true;
};

/** Ajuste IN-PLACE les blocs d'une séance pour respecter la tolérance ±TOLERANCE_MINUTES. */
export const fitSessionDuration = (session, targetMinutes) => {
  const targetSeconds = targetMinutes * 60;
  const tolerance = TOLERANCE_MINUTES * 60;
  let guard = 0;
  while (totalSeconds(session) > targetSeconds + tolerance && guard < 60) {
    if (!session.blocks.some((b) => adjustBlock(b, false))) break;
    guard += 1;
  }
  guard = 0;
  while (totalSeconds(session) < targetSeconds - tolerance && guard < 60) {
    if (!session.blocks.some((b) => adjustBlock(b, true))) break;
    guard += 1;
  }
  return {
    estimatedMinutes: Math.round(totalSeconds(session) / 60),
    insufficientAdjustRange: Math.abs(totalSeconds(session) - targetSeconds) > tolerance,
  };
};

/**
 * @param {object[]} methods - CARDIO_METHODS (depuis /api/tracking/cardio-pro/methods)
 * @param {object[]} library - exercices cardio réels (depuis /api/tracking/cardio-pro/exercise-library)
 * @param {string[]} preferredMethods - méthodes choisies par le client (vide = toutes)
 * @param {string[]} equipmentAllowed - équipements autorisés (vide = tous)
 * @param {number} daysPerWeek, {number} sessionMinutes, {string} level, {number} weeksTarget
 * @param {number} seed - décalage déterministe pour varier le résultat au clic "Régénérer"
 * @returns {object[]} sessions au format attendu par POST /api/tracking/cardio-pro
 */
export const buildGuidedCardioProgram = ({
  methods, library, preferredMethods = [], equipmentAllowed = [], daysPerWeek, sessionMinutes, level, weeksTarget, seed = 0,
}) => {
  const adj = LEVEL_ADJUST[level] || LEVEL_ADJUST.intermediaire;
  const usableMethods = preferredMethods.length ? methods.filter((m) => preferredMethods.includes(m.id)) : methods;
  const activeMethods = usableMethods.length ? usableMethods : methods;
  const filteredPool = (library || []).filter((e) => !equipmentAllowed.length || equipmentAllowed.includes(e.equipment));
  const basePool = filteredPool.length ? filteredPool : (library || []);

  const sessions = [];
  for (let i = 0; i < daysPerWeek; i += 1) {
    const sessionMethods = pickMethodsForSession(activeMethods, i);
    const warmup = sessionMinutes >= 25 ? 240 : 180;
    const cooldown = sessionMinutes >= 25 ? 150 : 120;
    const availableSeconds = Math.max(60, sessionMinutes * 60 - warmup - cooldown);
    const perBlockSeconds = Math.round(availableSeconds / Math.max(1, sessionMethods.length));
    const usedNames = new Set();
    // mélange déterministe (seedé) pour varier réellement les exercices d'une séance/régénération
    // à l'autre — le pool complet reste mélangé une seule fois par séance (offset seed+i).
    const rotatedPool = basePool.length ? seededShuffle(basePool, seed * 97 + i + 1) : [];
    const blocks = sessionMethods.map((m) => buildBlock(m, adj, rotatedPool, usedNames, perBlockSeconds));
    const session = {
      session_label: `Séance ${i + 1}`, sessions_target: Math.max(1, weeksTarget || 8),
      warmup_seconds: warmup, cooldown_seconds: cooldown, blocks,
    };
    fitSessionDuration(session, sessionMinutes);
    sessions.push(session);
  }
  return sessions;
};
